/**
 * LogoUpload Component
 *
 * A file upload control for adding a custom logo to the QR code center overlay.
 * Accepts common image formats (PNG, JPG, SVG) and validates file type and size.
 * Displays a preview thumbnail of the uploaded logo with a remove button.
 *
 * Requirements:
 * - 6.1: When center customization is enabled, display a logo upload control
 * - 6.2: Accept common image formats (PNG, JPG, SVG)
 * - 6.5: When both a logo and bay label are configured, render the logo with the bay label overlaid or adjacent
 * - 6.6: Provide a button to remove an uploaded logo
 *
 * Property 6: Image Format Validation - Accept files with MIME types image/png, image/jpeg, image/jpg, and image/svg+xml
 *
 * Error Handling:
 * - Invalid image format for logo: Display error toast, reject upload
 * - Logo file too large (>5MB): Display error toast, reject upload
 */

import { useCallback, useId, useRef, useState } from 'react';
import './LogoUpload.css';

/** Maximum file size in bytes (500KB) */
export const MAX_FILE_SIZE_BYTES = 500 * 1024;

/** Maximum file size in KB for display */
export const MAX_FILE_SIZE_KB = 500;

/** Accepted MIME types for logo upload */
export const ACCEPTED_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/svg+xml',
] as const;

/** Accepted file extensions for the file input */
export const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.svg';

/**
 * Props for the LogoUpload component
 */
export interface LogoUploadProps {
  /** Current logo as data URL, null if no logo uploaded */
  readonly logoDataUrl: string | null;
  /** Callback fired when a valid file is uploaded */
  readonly onUpload: (file: File) => void;
  /** Callback fired when the logo is removed */
  readonly onRemove: () => void;
}

/**
 * Error state for upload validation
 */
interface UploadError {
  readonly type: 'invalid_format' | 'file_too_large';
  readonly message: string;
}

/**
 * Validates if a file has an accepted MIME type
 */
export function isValidMimeType(mimeType: string): boolean {
  return ACCEPTED_MIME_TYPES.includes(mimeType);
}

/**
 * Validates if a file size is within the allowed limit
 */
export function isValidFileSize(sizeInBytes: number): boolean {
  return sizeInBytes <= MAX_FILE_SIZE_BYTES;
}

/**
 * LogoUpload - Logo image upload and preview
 *
 * Provides a file input for uploading logo images with validation,
 * a preview thumbnail of the uploaded logo, and a remove button.
 * Shows error messages for invalid file types or oversized files.
 *
 * @example
 * ```tsx
 * <LogoUpload
 *   logoDataUrl={logoDataUrl}
 *   onUpload={(file) => handleLogoUpload(file)}
 *   onRemove={() => handleLogoRemove()}
 * />
 * ```
 */
export function LogoUpload({
  logoDataUrl,
  onUpload,
  onRemove,
}: LogoUploadProps): JSX.Element {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<UploadError | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const validateAndUpload = useCallback(
    (file: File) => {
      // Clear any previous error
      setError(null);

      // Validate MIME type
      if (!isValidMimeType(file.type)) {
        setError({
          type: 'invalid_format',
          message: 'Invalid file format. Please upload a PNG, JPG, or SVG image.',
        });
        return;
      }

      // Validate file size
      if (!isValidFileSize(file.size)) {
        setError({
          type: 'file_too_large',
          message: `File is too large. Maximum size is ${MAX_FILE_SIZE_KB}KB.`,
        });
        return;
      }

      // File is valid, trigger upload
      onUpload(file);
    },
    [onUpload]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        validateAndUpload(file);
      }
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [validateAndUpload]
  );

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const file = event.dataTransfer.files?.[0];
      if (file) {
        validateAndUpload(file);
      }
    },
    [validateAndUpload]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveClick = useCallback(() => {
    setError(null);
    onRemove();
  }, [onRemove]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleUploadClick();
      }
    },
    [handleUploadClick]
  );

  return (
    <div className="logo-upload">
      <label className="logo-upload__label" htmlFor={inputId}>
        Logo Image
      </label>

      {logoDataUrl ? (
        // Logo preview state
        <div className="logo-upload__preview">
          <div className="logo-upload__thumbnail-wrapper">
            <img
              src={logoDataUrl}
              alt="Uploaded logo preview"
              className="logo-upload__thumbnail"
            />
          </div>
          <div className="logo-upload__preview-actions">
            <button
              type="button"
              className="logo-upload__change-button"
              onClick={handleUploadClick}
              aria-label="Change logo"
            >
              <UploadIcon />
              <span>Change</span>
            </button>
            <button
              type="button"
              className="logo-upload__remove-button"
              onClick={handleRemoveClick}
              aria-label="Remove logo"
            >
              <RemoveIcon />
              <span>Remove</span>
            </button>
          </div>
        </div>
      ) : (
        // Upload dropzone state
        <div
          className={`logo-upload__dropzone ${isDragging ? 'logo-upload__dropzone--dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleUploadClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Upload logo image"
        >
          <UploadIcon className="logo-upload__dropzone-icon" />
          <span className="logo-upload__dropzone-text">
            Drag & drop or click to upload
          </span>
          <span className="logo-upload__dropzone-hint">
            PNG, JPG, or SVG (max {MAX_FILE_SIZE_KB}KB)
          </span>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="logo-upload__input"
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleFileChange}
        aria-describedby={error ? `${inputId}-error` : undefined}
      />

      {/* Error message */}
      {error && (
        <div
          id={`${inputId}-error`}
          className="logo-upload__error"
          role="alert"
        >
          <ErrorIcon />
          <span>{error.message}</span>
          <button
            type="button"
            className="logo-upload__error-dismiss"
            onClick={clearError}
            aria-label="Dismiss error"
          >
            <CloseIcon />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Upload icon component
 */
function UploadIcon({ className = '' }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={`logo-upload__icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

/**
 * Remove/trash icon component
 */
function RemoveIcon(): JSX.Element {
  return (
    <svg
      className="logo-upload__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

/**
 * Error/warning icon component
 */
function ErrorIcon(): JSX.Element {
  return (
    <svg
      className="logo-upload__icon logo-upload__error-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

/**
 * Close/X icon component
 */
function CloseIcon(): JSX.Element {
  return (
    <svg
      className="logo-upload__icon logo-upload__close-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
